import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors, breakpoints, fadeIn } from '../../../assets/styles/GlobalStyles';
import { userProfileService } from '../services/userProfileService';
import { FaArrowLeft } from 'react-icons/fa';
import MainLayout from '../../../layouts/MainLayout';
import { ProfileHeader } from '../components/ProfileHeader';
import { AccountStatus } from '../components/AccountStatus';
import { OABStatus } from '../components/OABStatus';
import { ContactInfo } from '../components/ContactInfo';
import { ProfileAddress } from '../components/ProfileAddress';
import { ProfileSkeleton } from '../components/ProfileSkeleton';
import { ProfileError } from '../components/ProfileError';

const PageContainer = styled.div`
  height: 100vh;
  height: 100dvh;
  overflow-y: auto; /* Libera scroll vertical */
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 300px;
    background: linear-gradient(135deg, ${colors.blueDark} 0%, ${colors.blueMedium} 100%);
    opacity: 0.05;
    pointer-events: none;
    z-index: 0;
  }
`;

const ProfileContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 120px 2rem 2rem 2rem;
  animation: ${fadeIn} 0.5s ease-out;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 100px 1.5rem 1.5rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 90px 1rem 1rem 1rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  color: ${colors.darkBlue};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 1.5rem;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  svg {
    font-size: 1rem;
    transition: transform 0.2s ease;
  }

  &:hover {
    color: ${colors.lightBlue};
    
    svg {
      transform: translateX(-3px);
    }
  }
`;

const ProfileCard = styled.div`
  background: ${colors.white};
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);

  &:hover {
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
`;

const useProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProfile = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await userProfileService.getProfile();
      if (response.success) {
        setProfile(response.usuario);
      } else {
        setError(response.error || 'Erro ao carregar perfil');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  return { profile, loading, error, loadProfile };
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { profile, loading, error, loadProfile } = useProfile();

  const handleGoBack = () => navigate('/home');

  if (loading) {
    return (
      <MainLayout>
        <PageContainer>
          <ProfileContainer>
            <ProfileSkeleton />
          </ProfileContainer>
        </PageContainer>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <PageContainer>
          <ProfileContainer>
            <ProfileError error={error} onRetry={loadProfile} />
          </ProfileContainer>
        </PageContainer>
      </MainLayout>
    );
  }

  if (!profile) {
    return (
      <MainLayout>
        <PageContainer>
          <ProfileContainer>
            <ProfileError error="Nenhum dado encontrado" onRetry={loadProfile} />
          </ProfileContainer>
        </PageContainer>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <PageContainer>
        <ProfileContainer>
          <BackButton onClick={handleGoBack}>
            <FaArrowLeft /> Voltar para Home
          </BackButton>
          
          <ProfileCard>
            <ProfileHeader nome={profile.nome} documento={profile.documento} />
            <AccountStatus
              ativo={profile.ativo}
              ultimoLogin={profile.ultimoLogin}
              dataCadastro={profile.dataCadastro}
            />
            <OABStatus
              tipoInscricao={profile.tipoInscricao}
              situacaoOAB={profile.situacaoOAB}
            />
            <ContactInfo contato={profile.contato} />
            <ProfileAddress endereco={profile.endereco} />
          </ProfileCard>
        </ProfileContainer>
      </PageContainer>
    </MainLayout>
  );
};

export default ProfilePage;